<template>
  <LinkElement
    ref="el"
    :defaultElement="submitForm ? 'button' : 'a'"
    :routerLink="routerLink"
    :href="href"
    :class="[
      $style.base,
      $style[size],
      $style[color],
      {
        [$style.isDisabled]: disabled,
        [$style.isSelected]: selected,
        [$style.isLoading]: loading,
        [$style.fullWidth]: fullWidth,
        [$style.mobile]: $screen === 's'
      }
    ]"
    @click="onClick"
  >
    <div :class="$style.inner">
      <BaseSpinner
        v-if="loading"
        :color="color === 'inverted' ? 'default' : 'inverted'"
      />
      <div
        v-if="icon || href"
        :class="$style.icon"
      >
        <BaseIcon
          :name="icon || (href ? 'external_link' : undefined) as IconName"
          color="inherit"
        />
      </div>
      <div :class="$style.content">
        <slot />
      </div>
      <ChevronAnimated
        v-if="options && options.length"
        :class="$style.chevron"
        :reversed="showPopover"
        color="inherit"
      />
      <transition name="pop-small">
        <div
          v-if="notification"
          :class="$style.notification"
        >
          <BaseNotification
            :color="color === 'inverted' ? 'primary' : 'inverted'"
            size="m"
          >
            {{ notification }}
          </BaseNotification>
        </div>
      </transition>
    </div>
    <Popover
      v-if="options && options.length && mounted"
      :visible="showPopover"
      :options="options"
      :parentEl="el"
      :class="$style.popover"
      @select="$emit('select', $event)"
      @close="showPopover = false"
    />
  </LinkElement>
</template>

<script lang="ts">
export default {
  name: 'BaseButton',
  inheritAttrs: false,
};
</script>

<script
  setup
  lang="ts"
>
import { onMounted, ref, watch } from 'vue';
import type { LocationQueryRaw } from 'vue-router';
import LinkElement from '@/components/_shared/Link.vue';
import type { IconName } from '@/components/base-icon/types';
import ChevronAnimated from '../_shared/ChevronAnimated.vue';
import Popover from './Popover.vue';
import type { Option } from './types';

interface BaseButtonProps {
  color?: 'primary' | 'warning' | 'inverted' | 'error';
  disabled?: boolean;
  fullWidth?: boolean;
  href?: string;
  icon?: IconName;
  loading?: boolean;
  notification?: number;
  options?: Option[];
  routerLink?: LocationQueryRaw | string;
  selected?: boolean;
  size?: 'm' | 'l';
  submitForm?: boolean;
  autoWidth?: boolean;
}

const emit = defineEmits(['click', 'select']);
const props = withDefaults(defineProps<BaseButtonProps>(), {
  color: 'primary',
  size: 'm',
});

const el = ref();
const showPopover = ref(false);

const onClick = () => {
  if (props.disabled) {
    return;
  }

  emit('click');

  if (props.options?.length) {
    showPopover.value = !showPopover.value;
  }
};

watch(
  () => props.disabled,
  () => {
    showPopover.value = false;
  },
);

const mounted = ref(false);
onMounted(() => (mounted.value = true));
</script>

<style
  lang="scss"
  module
>
.base {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-width: 42px;
  min-height: $input-height;
  border: 1px solid transparent;
  border-radius: $radius;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background-color 0.15s ease,
    transform 0.15s ease;
  user-select: none;
  text-decoration: none;
  flex-shrink: 0;
  gap: $spacing * 0.5;

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 4px var(--focus-ring);
  }

  // Pressed: instant scale-down, eases back on release. Not while an option
  // in the popover is pressed: the transform makes the button a stacking
  // context, so content below it covers the popover and the tap is lost.
  &:active:not(.isDisabled):not(.isLoading):not(:has(.popover:active)) {
    transform: scale(0.97);
    transition-duration: 0s;

    @media (prefers-reduced-motion: reduce) {
      transform: none;
    }
  }

  &.m {
    padding: 10px $spacing;
  }

  &.l {
    min-height: 54px;
    padding: 14px 22px;
    font-size: 16px;
  }

  &.primary {
    background-color: var(--action-primary);
    color: var(--text-on-action-primary);

    @include hover {
      background-color: var(--action-primary-hover);
    }

    &:active {
      background-color: var(--action-primary-active);
    }
  }

  // Opaque surface so the button stays readable on coloured backgrounds
  // (navy banners); the translucent design tints are layered on top of it.
  &.inverted {
    background-color: var(--surface-primary);
    border-color: var(--border-strong);
    color: var(--text-primary);

    @include hover {
      background-image: linear-gradient(var(--highlight), var(--highlight));
    }

    &:active {
      background-image: linear-gradient(
        var(--highlight-strong),
        var(--highlight-strong)
      );
    }
  }

  // Warning and error hold their hover colour while pressed. The hover fills
  // use primitives because --warning-dark and --error-dark get lighter in
  // dark mode.
  &.warning {
    background-color: var(--warning-base);
    color: var(--text-primary-inverted);

    @include hover {
      background-color: var(--color-orange-700);
    }

    &:active {
      background-color: var(--color-orange-700);
    }

    &:focus-visible {
      box-shadow: 0 0 0 4px var(--focus-ring-warning);
    }
  }

  &.error {
    background-color: var(--error-base);
    color: var(--text-primary-inverted);

    @include hover {
      background-color: var(--color-red-700);
    }

    &:active {
      background-color: var(--color-red-700);
    }

    &:focus-visible {
      box-shadow: 0 0 0 4px var(--focus-ring-error);
    }
  }

  &.isSelected {
    background-image: none;
    background-color: var(--action-primary);
    border-color: transparent;
    color: var(--text-on-action-primary);

    @include hover {
      background-image: none;
      background-color: var(--action-primary-hover);
    }

    &:active {
      background-image: none;
      background-color: var(--action-primary-active);
    }
  }

  &.isDisabled {
    pointer-events: none;
    background-image: none;
    background-color: var(--neutral-softer);
    border-color: var(--border);
    color: var(--text-placeholder);
  }

  &.fullWidth {
    width: 100%;
  }

  &.isLoading {
    pointer-events: none;
    color: transparent;
  }
}

.inner {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 20px;
  text-align: center;

  .base:not(.inverted) &,
  .base.isSelected & {
    font-weight: bold;
  }
}

.icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;

  // Icon-only buttons stay square
  &:has(+ .content:empty) {
    margin: 0 -6px;
  }

  // The icon and label share .inner, which has no gap of its own
  & + .content:not(:empty) {
    margin-left: $spacing * 0.5;
  }
}

// The outline button's icons are one step quieter than its label
.icon,
.chevron {
  .base.inverted:not(.isSelected):not(.isDisabled) & {
    color: var(--text-secondary);
  }

  // Primary buttons turn white in dark mode, so the white icon would disappear
  .base.primary:not(:hover) & :global(:is(path, circle, rect)) {
    @include theme('dark') {
      fill: var(--color-text-fixed);
    }
  }
}

.notification {
  margin-left: $spacing * 0.5;
}

.content:empty + .notification,
.content:empty + * + .notification {
  margin-right: -6px;
}

.content {
  &:empty {
    display: none;

    & + .chevron {
      display: none;
    }
  }

  .base.l:not(.mobile):not(.fullWidth) & {
    min-width: 209px;
  }
}

.content,
.icon,
.chevron {
  .base.isLoading & {
    opacity: 0;
    visibility: hidden;
  }
}
</style>
